import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';

const MODAL_WIDTH = 220;

const defaultGetMentionHtml = (item) =>
    `<span data-id="${item?.defaultValue}" data-key="${item?.defaultValue}" contenteditable="false" class="mention">${item?.token}</span>&nbsp;`;

export const MentionableInput = forwardRef(
    (
        {
            value,
            defaultValue,
            onChange,
            readOnly,
            mentionOptions,
            onMentionInsert,
            getMentionHtml = defaultGetMentionHtml,
            className,
            placeholder,
            singleLine = false,
            noError,
            error,
        },
        ref
    ) => {
        const internalRef = useRef(null);
        const mentionItemRefs = useRef([]);
        const [open, setOpen] = useState(false);
        const [activeIndex, setActiveIndex] = useState(0);
        const [position, setPosition] = useState({ top: 0, left: 0 });
        const [searchQuery, setSearchQuery] = useState('');
        const [isEmpty, setIsEmpty] = useState(true);

        const checkEmpty = () => {
            if (!internalRef.current) return true;
            const text = internalRef.current.textContent || '';
            return text.replace(/\u00A0/g, '').trim().length === 0
                && !internalRef.current.querySelector('.mention, img');
        };

        useImperativeHandle(ref, () => internalRef.current);

        const handleInput = () => {
            if (readOnly) return;
            const html = internalRef.current?.innerHTML || '';

            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const rawTextContent = range.startContainer.textContent || '';
                const textContent = rawTextContent.replace(/\u00A0/g, ' ');
                const offset = range.startOffset;

                const textBeforeCursor = textContent.slice(0, offset);
                const lastAtPos = textBeforeCursor.lastIndexOf('@');

                if (lastAtPos !== -1) {
                    const query = textBeforeCursor.slice(lastAtPos + 1);
                    setSearchQuery(query);

                    // If modal is closed, check if query matches any options and reopen
                    if (!open) {
                        const hasMatches = mentionOptions?.some(
                            (option) =>
                                option?.name?.toLowerCase().includes(query.toLowerCase()) ||
                                option?.token?.toLowerCase().includes(query.toLowerCase())
                        );
                        if (hasMatches) {
                            openModalAtCursor();
                            setSearchQuery(query);
                        }
                    }
                } else if (open) {
                    setOpen(false);
                }
            }

            onChange(html);
            setIsEmpty(checkEmpty());
        };

        const openModalAtCursor = () => {
            if (readOnly) return;
            setSearchQuery('');
            const selection = window.getSelection();
            if (!selection.rangeCount) return;

            const range = selection.getRangeAt(0).cloneRange();
            range.collapse(false);

            const caretRect = range.getBoundingClientRect();

            const left = Math.min(caretRect.left, window.innerWidth - MODAL_WIDTH - 16);

            setPosition({
                top: caretRect.bottom + 6,
                left: left,
            });

            setOpen(true);
            setActiveIndex(0);
        };

        const insertMention = (item) => {
            if (readOnly) return;
            const selection = window.getSelection();
            if (!selection.rangeCount) return;

            const range = selection.getRangeAt(0);

            // Resolve the text node that contains the @ character.
            // On non-first lines, browsers wrap each line in a <div>, so the
            // range container may be an element node (offsets = child indices).
            let container = range.startContainer;
            let offset = range.startOffset;

            if (container.nodeType === Node.ELEMENT_NODE) {
                const child = container.childNodes[Math.max(offset - 1, 0)];
                if (child && child.nodeType === Node.TEXT_NODE) {
                    container = child;
                    offset = child.textContent.length;
                } else if (child) {
                    const walker = document.createTreeWalker(child, NodeFilter.SHOW_TEXT);
                    let lastText = null;
                    while (walker.nextNode()) lastText = walker.currentNode;
                    if (lastText) {
                        container = lastText;
                        offset = lastText.textContent.length;
                    }
                }
            }

            const textContent = container.textContent || '';
            const textBeforeCursor = textContent.slice(0, offset);
            const lastAtPos = textBeforeCursor.lastIndexOf('@');

            if (lastAtPos === -1) {
                setOpen(false);
                return;
            }

            // Create a range that selects the @ character and the typed query
            const deleteRange = document.createRange();
            deleteRange.setStart(container, lastAtPos);
            deleteRange.setEnd(container, offset);
            deleteRange.deleteContents();

            // Build DOM nodes from the mention HTML
            const html = getMentionHtml(item);
            const temp = document.createElement('template');
            temp.innerHTML = html;
            const frag = temp.content;
            const lastNode = frag.lastChild;
            deleteRange.insertNode(frag);

            // Move the cursor to after the inserted mention
            if (lastNode) {
                const cursorRange = document.createRange();
                cursorRange.setStartAfter(lastNode);
                cursorRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(cursorRange);
            }

            setOpen(false);
            setSearchQuery('');
            onMentionInsert?.(item);
            handleInput();
        };

        const filteredOptions =
            mentionOptions?.filter((option) => {
                const query = searchQuery.toLowerCase();
                return option?.name?.toLowerCase().includes(query) || option?.token?.toLowerCase().includes(query);
            }) || [];

        useEffect(() => {
            if (activeIndex >= filteredOptions.length) {
                setActiveIndex(Math.max(0, filteredOptions.length - 1));
            }
        }, [filteredOptions.length]);

        useEffect(() => {
            const el = mentionItemRefs.current[activeIndex];
            if (el) {
                el.scrollIntoView({ block: 'nearest' });
            }
        }, [activeIndex]);

        const handleKeyDown = (e) => {
            if (readOnly) {
                e.preventDefault();
                return;
            }

            // Prevent new lines in single-line mode
            if (singleLine && e.key === 'Enter' && !open) {
                e.preventDefault();
                return;
            }

            if (e.key === '@') {
                e.preventDefault();
                document.execCommand('insertText', false, '@');
                openModalAtCursor();
                return;
            }

            if (!open) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setActiveIndex((i) => (i + 1) % filteredOptions?.length);
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    setActiveIndex((i) => (i === 0 ? filteredOptions?.length - 1 : i - 1));
                    break;

                case 'Enter':
                    e.preventDefault();
                    if (filteredOptions.length > 0) {
                        insertMention(filteredOptions[activeIndex]);
                    }
                    break;

                case 'Escape':
                    setOpen(false);
                    break;

                case ' ':
                    // Prevent macOS/iOS double-space-to-period auto-replace
                    // in contentEditable by manually inserting a space
                    e.preventDefault();
                    document.execCommand('insertText', false, ' ');
                    break;

                case 'Backspace':
                    const selection = window.getSelection();
                    if (selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        const textContent = range.startContainer.textContent || '';
                        // Need to check the text BEFORE considering \u00A0 replacement on input
                        const offset = range.startOffset;
                        // If we are deleting the '@' character itself
                        if (offset > 0 && textContent[offset - 1] === '@') {
                            setOpen(false);
                        }
                    }
                    break;

                default:
                    break;
            }
        };

        useEffect(() => {
            if (value === undefined) return; // Uncontrolled mode: ignore value updates
            if (internalRef.current && (value || '') !== internalRef.current.innerHTML) {
                internalRef.current.innerHTML = value || '';
                setIsEmpty(checkEmpty());
            }
        }, [value]);

        useEffect(() => {
            if (internalRef.current && !internalRef.current.innerHTML) {
                internalRef.current.innerHTML = value || defaultValue || '';
            }
            setIsEmpty(checkEmpty());
        }, []);

        // Sync defaultValue when it changes externally (e.g. undo/redo)
        useEffect(() => {
            if (value !== undefined) return; // Controlled mode uses the value effect above
            if (internalRef.current && (defaultValue || '') !== internalRef.current.innerHTML) {
                internalRef.current.innerHTML = defaultValue || '';
                setIsEmpty(checkEmpty());
            }
        }, [defaultValue]);

        return (
            <div style={{ width: '100%', overflow: 'hidden', fontFamily: 'Open Sans, sans-serif', position: 'relative' }}>
                {isEmpty && placeholder && (
                    <span className="mention-placeholder">{placeholder}</span>
                )}
                <div
                    ref={internalRef}
                    contentEditable={!readOnly}
                    suppressContentEditableWarning
                    className={className || ''}
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onBlur={() => {
                        setTimeout(() => setOpen(false), 200);
                    }}
                />
                {!noError && <p className="error-message">{error}</p>}

                {open &&
                    !!filteredOptions?.length &&
                    ReactDOM.createPortal(
                        <>
                            <div
                                className="mention-back-drop"
                                onClick={() => setOpen(false)}
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: '100vw',
                                    height: '100vh',
                                    zIndex: 9998,
                                }}
                            />
                            <div
                                className="mention-modal"
                                style={{
                                    top: position.top,
                                    left: position.left,
                                    position: 'fixed',
                                    zIndex: 9999,
                                    backgroundColor: '#fff',
                                    border: '1px solid #ddd',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                    borderRadius: '6px',
                                    width: MODAL_WIDTH,
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    padding: '4px 0',
                                }}
                            >
                                {filteredOptions?.map((item, index) => (
                                    <div
                                        key={index}
                                        ref={(el) => (mentionItemRefs.current[index] = el)}
                                        className={`modal-item ${index === activeIndex ? 'active' : ''}`}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => insertMention(item)}
                                        style={{
                                            fontFamily: 'Open Sans, sans-serif',
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            color: '#172B4D',
                                            textAlign: 'left',
                                            backgroundColor: index === activeIndex ? '#eef4ff' : 'white',
                                            lineHeight: 'normal',
                                        }}
                                    >
                                        {item?.name}
                                    </div>
                                ))}
                            </div>
                        </>,
                        document.body
                    )}
            </div>
        );
    }
);
